import React, { Fragment, useState } from 'react';
import { injectIntl } from 'react-intl';
import jsQR from 'jsqr';
import { Button, Grid, IconButton, TextField, Typography, InputAdornment, Input } from '@mui/material';
import TodayIcon from '@mui/icons-material/Today';
import SelectButtonGroup from '../../../../components/SelectButtonGroup/SelectButtonGroup';
import { PhoneNumberInputWrapper } from '../../../NewSignUp/contents/PhoneNumberInputWrapper';
import moment from 'moment';
import { DatePicker } from '../../../../components/ClickableField/ClickableField';
import MyDetailsProfilePicture from './MyDetailsProfilePicture/MyDetailsProfilePicture';
import _ from 'lodash';
import { THEME_COLOR_PALETTES, getValidThemeColorOption } from '../../../../constants/my-details';
import { getErrorMessageIntl } from '../../../../utils/validators';
import { getAvatar } from '../../../../utils/my-details';
import AvatarPickerDialog from './AvatarPickerDialog/AvatarPickerDialog';
import AladdinHint from '../../../../components/AladdinHint/AladdinHint';
import { formatTwoRowsString, getUserInitial } from '../../../../utils/user';
import DISCSelect from '../../../../components/DISCSelect/DISCSelect';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import ImageIcon from '@mui/icons-material/Image';
import { getAgeFromDob } from '../../../../utils/date';
import DISCDialogComponent from '../../../../components/ClientEntry/DISCDialog/DISCDialogComponent/DISCDialogComponent';
import WechatQrCodeDescriptionDialog from './WechatQrCodeDescriptionDialog/WechatQrCodeDescriptionDialog';

const ProfileImageContainerStyle = {
  padding: 8,
  border: '8px solid #3966f870'
};

const ProfileImageStyle = {
  width: 100
};

const ProfileImageUploadTypoStyle = {
  textDecoration: 'underline'
};

const MyDetailsProfile = props => {
  const { intl, details, error, onChangeDetails } = props;

  const [avatarDialog, setAvatarDialog] = useState(false);
  const [personalityDialog, setPersonalityDialog] = useState(false);
  const [wechatQrCodeDescriptionDialogOpen, setWechatQrCodeDescriptionDialogOpen] = useState(false);
  const [wechatUrlError, setWechatUrlError] = useState('');

  const {
    name,
    gender,
    email,
    workEmail,
    phoneNumber,
    phoneNumber2,
    whatsapp,
    wechatId,
    wechatUrl,
    lineId,
    dob,
    age,
    personality
  } = details || {};

  const themeColor = getValidThemeColorOption(details);
  const initial = getUserInitial(details);
  const avatar = getAvatar(details);
  const formattedAge = getAgeFromDob(dob, age) || '';

  const createSimpleOnChangeHandler = (key, emptyValue, formatter = val => val) => event =>
    onChangeDetails({ [key]: formatter(event.target.value || emptyValue) });
  const onChangeAvatar = src => onChangeDetails({ avatar: src });
  const onChangeName = createSimpleOnChangeHandler('name', '', formatTwoRowsString);
  const onChangeGender = createSimpleOnChangeHandler('gender', null);
  const onChangeEmail = createSimpleOnChangeHandler('email', '');
  const onChangeWorkEmail = createSimpleOnChangeHandler('workEmail', '');
  const onChangePhoneNumber = createSimpleOnChangeHandler('phoneNumber', '');
  const onChangePhoneNumber2 = createSimpleOnChangeHandler('phoneNumber2', '');
  const onChangeWhatsapp = createSimpleOnChangeHandler('whatsapp', '');
  const onChangeWechatId = createSimpleOnChangeHandler('wechatId', '');
  const onChangeLineId = createSimpleOnChangeHandler('lineId', '');
  const onChangeDob = date => {
    const updates = { dob: date };
    if (date) {
      updates.age = moment().diff(moment(moment(date).format('DD-MM-YYYY'), 'DD-MM-YYYY'), 'years');
    }
    onChangeDetails(updates);
  };
  const onChangeAge = createSimpleOnChangeHandler('age', '');
  const onChangePersonality = personality => onChangeDetails({ personality: personality || '' });

  const openAvatarDialog = () => setAvatarDialog(true);
  const closeAvatarDialog = () => setAvatarDialog(false);

  const openPersonalityDialog = () => setPersonalityDialog(true);
  const closePersonalityDialog = () => setPersonalityDialog(false);

  const handleQrCodeUpload = async event => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const reader = new FileReader();

      reader.onload = e => {
        const image = new Image();
        image.src = e.target.result;

        image.onload = () => {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          // Resize the image for better QR detection
          const maxDimension = 500; // Limit the maximum dimension to 500px
          const scale = Math.min(maxDimension / image.width, maxDimension / image.height, 1);
          canvas.width = image.width * scale;
          canvas.height = image.height * scale;

          context.drawImage(image, 0, 0, canvas.width, canvas.height);

          // Convert to grayscale for better QR detection
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          for (let i = 0; i < imageData.data.length; i += 4) {
            const r = imageData.data[i];
            const g = imageData.data[i + 1];
            const b = imageData.data[i + 2];
            const gray = 0.299 * r + 0.587 * g + 0.114 * b;
            imageData.data[i] = gray; // Red
            imageData.data[i + 1] = gray; // Green
            imageData.data[i + 2] = gray; // Blue
          }

          // Invert colors: Dark QR on light background
          for (let i = 0; i < imageData.data.length; i += 4) {
            imageData.data[i] = 255 - imageData.data[i]; // Red inversion
            imageData.data[i + 1] = 255 - imageData.data[i + 1]; // Green inversion
            imageData.data[i + 2] = 255 - imageData.data[i + 2]; // Blue inversion
          }

          context.putImageData(imageData, 0, 0);

          // Pass the preprocessed image to jsQR
          const code = jsQR(imageData.data, canvas.width, canvas.height);

          if (code?.data && code.data.includes('wechat.com')) {
            onChangeDetails({ wechatUrl: code.data });
            setWechatUrlError('');
          } else {
            onChangeDetails({ wechatUrl: '' });
            setWechatUrlError(intl.formatMessage({ id: 'unable-to-detect-wechat-qr-code' }));
          }
        };
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error decoding QR code:', error);
      onChangeDetails({ wechatUrl: '' });
      setWechatUrlError(intl.formatMessage({ id: 'unable-to-detect-wechat-qr-code' }));
    }
  };

  return (
    <Fragment>
      <AvatarPickerDialog
        open={avatarDialog}
        onClose={closeAvatarDialog}
        initial={initial}
        avatar={avatar}
        onChange={onChangeAvatar}
        borderColor={THEME_COLOR_PALETTES[themeColor][1]}
      />
      <DISCDialogComponent
        open={personalityDialog}
        onClose={closePersonalityDialog}
        contentTextType="adviser"
        personality={personality}
        onSubmit={onChangePersonality}
      />
      <WechatQrCodeDescriptionDialog
        open={wechatQrCodeDescriptionDialogOpen}
        onClose={() => setWechatQrCodeDescriptionDialogOpen(false)}
      />
      <Grid container spacing={3} sx={{ flexDirection: 'column' }}>
        <Grid>
          <AladdinHint title={intl.formatMessage({ id: 'mda-profile-title' })} />
        </Grid>
        <Grid>
          <Grid container spacing={1} wrap="nowrap">
            <Grid>
              <Grid container sx={{ flexDirection: 'column', alignItems: 'center' }}>
                <Grid onClick={openAvatarDialog}>
                  <MyDetailsProfilePicture
                    initial={initial}
                    src={avatar}
                    width={150}
                    borderColor={THEME_COLOR_PALETTES[themeColor][1]}
                  />
                </Grid>
                <Grid>
                  <Button fullWidth={true} onClick={openAvatarDialog}>
                    <Typography color="textSecondary" style={ProfileImageUploadTypoStyle}>
                      {intl.formatMessage({ id: 'mda-avatar-change' })}
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid style={{ flexGrow: 1 }}>
              <Grid container spacing={2} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Grid style={{ width: '100%' }}>
                  <TextField
                    label={`${intl.formatMessage({ id: 'Name' })}*`}
                    value={name}
                    onChange={onChangeName}
                    error={!!error.name}
                    helperText={error.name && intl.formatMessage({ id: getErrorMessageIntl(error.name) })}
                    multiline={true}
                    rows={2}
                  />
                </Grid>
                <Grid>
                  <SelectButtonGroup
                    choices={[
                      {
                        label: intl.formatMessage({ id: 'Male' }),
                        value: 'Male',
                        style: {
                          minWidth: 64
                        }
                      },
                      {
                        label: intl.formatMessage({ id: 'Female' }),
                        value: 'Female',
                        style: {
                          minWidth: 64
                        }
                      }
                    ]}
                    value={gender}
                    onChange={onChangeGender}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <TextField
            label={`${intl.formatMessage({ id: 'Login Email' })}*`}
            value={email}
            onChange={onChangeEmail}
            error={!!error.email}
            helperText={error.email && intl.formatMessage({ id: getErrorMessageIntl(error.email) })}
            disabled
          />
        </Grid>
        <Grid>
          <TextField
            label={intl.formatMessage({ id: 'Work Email' })}
            value={workEmail}
            onChange={onChangeWorkEmail}
            error={!!error.workEmail}
            helperText={error.workEmail && intl.formatMessage({ id: getErrorMessageIntl(error.workEmail) })}
          />
        </Grid>
        <Grid>
          <TextField
            className="phone-text-field"
            name="phoneNumber"
            label={`${intl.formatMessage({ id: 'Tel' })}*`}
            value={phoneNumber}
            onChange={onChangePhoneNumber}
            error={!!error.phoneNumber}
            helperText={error.phoneNumber && intl.formatMessage({ id: getErrorMessageIntl(error.phoneNumber) })}
            slotProps={{
              input: {
                inputComponent: PhoneNumberInputWrapper,
                inputProps: {
                  intl: intl
                }
              },

              inputLabel: { shrink: false, className: 'non-shrink' }
            }}
          />
        </Grid>
        <Grid>
          <TextField
            className="phone-text-field"
            name="phoneNumber2"
            label={intl.formatMessage({ id: 'Tel 2' })}
            value={phoneNumber2}
            onChange={onChangePhoneNumber2}
            slotProps={{
              input: {
                inputComponent: PhoneNumberInputWrapper,
                inputProps: {
                  intl: intl
                }
              },

              inputLabel: { shrink: false, className: 'non-shrink' }
            }}
          />
        </Grid>
        <Grid>
          <TextField
            className="phone-text-field"
            name="whatsapp"
            label={intl.formatMessage({ id: 'WhatsApp' })}
            value={whatsapp}
            onChange={onChangeWhatsapp}
            slotProps={{
              input: {
                inputComponent: PhoneNumberInputWrapper,
                inputProps: {
                  intl: intl
                }
              },

              inputLabel: { shrink: false, className: 'non-shrink' }
            }}
          />
        </Grid>
        <Grid>
          <Grid
            container
            spacing={1}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid size={6}>
              <TextField label={intl.formatMessage({ id: 'Wechat ID' })} value={wechatId} onChange={onChangeWechatId} />
            </Grid>
            <Grid size={6}>
              <TextField label={intl.formatMessage({ id: 'Line ID' })} value={lineId} onChange={onChangeLineId} />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid
            container
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Grid>
              <Grid
                container
                spacing={1}
                sx={{
                  alignItems: 'center'
                }}
              >
                <Grid>
                  <Typography>{intl.formatMessage({ id: 'wechat-qr-code' })}</Typography>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    onClick={() => setWechatQrCodeDescriptionDialogOpen(true)}
                    color="primary"
                    style={{ padding: '0 4px', minWidth: 'unset' }}
                  >
                    {intl.formatMessage({ id: 'tutorial' })}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid>
              {wechatUrl ? (
                <Button variant="outlined" onClick={() => onChangeDetails({ wechatUrl: '' })} color="primary">
                  {intl.formatMessage({ id: 'delete' })}
                </Button>
              ) : (
                <Button component="label" variant="outlined" startIcon={<ImageIcon />}>
                  {intl.formatMessage({ id: 'upload' })}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleQrCodeUpload} />
                </Button>
              )}
            </Grid>
          </Grid>
          {wechatUrl ? (
            <Typography style={{ color: '#4caf50' }}>
              ✅ {intl.formatMessage({ id: 'wechat-qr-code-uploaded' })}
            </Typography>
          ) : (
            !wechatUrlError && <Typography>❌ {intl.formatMessage({ id: 'no-wechat-qr-code-uploaded' })}</Typography>
          )}
          {wechatUrlError && <Typography color="error">{wechatUrlError}</Typography>}
        </Grid>
        <Grid>
          <Grid
            container
            spacing={1}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid size={6}>
              <DatePicker
                name="dob"
                format="DD-MMM-YYYY"
                maxDate={moment()}
                value={!dob ? null : moment.parseZone(dob)}
                openTo="year"
                onChange={onChangeDob}
                referenceDate={moment().subtract(30, 'years')}
                label={intl.formatMessage({ id: 'Date of Birth' })}
                slotProps={{
                  field: { clearable: true }
                }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label={intl.formatMessage({ id: 'Age' })}
                value={formattedAge}
                disabled={!!dob}
                onChange={onChangeAge}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid
            container
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Grid>
              <Grid
                container
                spacing={1}
                sx={{
                  alignItems: 'center'
                }}
              >
                <Grid>
                  <Typography>{intl.formatMessage({ id: 'Personality' })}</Typography>
                </Grid>
                <Grid>
                  <IconButton color="primary" size="small" onClick={openPersonalityDialog}>
                    <HelpOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid>
              <DISCSelect personality={personality} onChange={onChangePersonality} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default injectIntl(MyDetailsProfile);
